import { useEffect, useEffectEvent, useMemo, useState, useRef } from 'react';
import { useParams } from 'react-router';
import { FrownOutlined } from '@ant-design/icons';
import { useQuery } from '@apollo/client/react';

import QueryBoundary from 'components/QueryBoundary/QueryBoundary';
import SectionMessage from 'components/SectionMessage/SectionMessage';
import useModalControls from 'hooks/useModalControls';
import useThirdPartyInfo from 'hooks/useThirdPartyInfo';
import { GET_SETTLEMENT_OFFERS } from 'queries/settlements';
import { GET_UPCOMING_PAYMENT_ACTIVITY } from 'queries/account';
import { SETTLEMENT_OFFER_STATES, STATUS } from 'utilities/constants';

import ActiveOffer from './ActiveOffer';
import OfferSetup from './OfferSetup';
import OfferEligibilityModal from '../../OfferEligibilityModal';
import {
  useRecoveriesState,
  resetRecoveriesState,
  setOffer,
} from './recoveriesState';

const RecoveriesSection = () => {
  const { currentStep, offer } = useRecoveriesState();
  const { accountId, customerId } = useParams();

  const [offers, setOffers] = useState([]);

  const [eligibilityChecks, setEligibilityChecks] = useState([]);
  const offerEligibilityModal = useModalControls();

  const [hasEligibleSettlementsOffer, setHasEligibleSettlementsOffer] =
    useState(false);
  const [
    hasInitiatedSettlementOfferFromAppWeb,
    setHasInitiatedSettlementOfferFromAppWeb,
  ] = useState(false);
  const [hasSettlementOffer, setHasSettlementOffer] = useState(false);
  const [hasScheduledPayment, setHasScheduledPayment] = useState(false);
  const [hasPendingPayment, setHasPendingPayment] = useState(false);
  const [hasExpiredOffer, setHasExpiredOffer] = useState(false);
  const [hasCompletedOffer, setHasCompletedOffer] = useState({
    offerType: null,
  });

  const settlementsQuery = useQuery(GET_SETTLEMENT_OFFERS, {
    variables: { customerId, accountId },
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
  });

  const processSettlementsOffersData = useEffectEvent((data) => {
    const account = data.account;
    const eligibility = account?.offers?.eligibility?.[0];
    const eligibilityChecks = eligibility
      ? eligibility.checks.filter(
          (check) =>
            check.name !==
            'Account does not have an existing active Settlement Offer with Mission Lane',
        )
      : [];
    setEligibilityChecks(eligibilityChecks);
    setHasEligibleSettlementsOffer(!!eligibility?.eligible);

    const settlementOffer = account?.offers?.offers?.find((offer) => {
      const activeOffer =
        offer?.data.state === SETTLEMENT_OFFER_STATES.IN_PROGRESS;
      const pendingCloseOutOffer =
        offer?.data.state === SETTLEMENT_OFFER_STATES.PENDING_CLOSE_OUT;
      const fulfilledOffer =
        offer?.data.state === SETTLEMENT_OFFER_STATES.FULFILLED;

      return activeOffer || pendingCloseOutOffer || fulfilledOffer;
    });

    if (settlementOffer) {
      setOffer({
        offer: settlementOffer,
      });
      setHasSettlementOffer(true);
    }

    const hasInitiatedOffer = account?.offers?.offers?.find(
      (offer) =>
        offer?.data.state === SETTLEMENT_OFFER_STATES.READY ||
        offer?.data.state === SETTLEMENT_OFFER_STATES.NOT_READY,
    );

    if (hasInitiatedOffer) {
      setHasInitiatedSettlementOfferFromAppWeb(true);
    }

    const hasCompletedOffer = account?.offers?.offers?.find(
      (offer) => offer?.data.state === SETTLEMENT_OFFER_STATES.FULFILLED,
    );

    if (hasCompletedOffer) {
      setHasCompletedOffer({
        offerType: hasCompletedOffer.data.subType,
      });
    }

    const hasExpiredOffer = account?.offers?.offers?.find(
      (offer) =>
        offer?.data.acceptedAt &&
        offer?.data.state === SETTLEMENT_OFFER_STATES.EXPIRED,
    );

    if (hasExpiredOffer) {
      setHasExpiredOffer(true);
    }

    const offersWithIndex = account?.offers?.settlement?.available?.map(
      (offer, index) => {
        return {
          ...offer,
          key: index + 1,
        };
      },
    );

    setOffers(offersWithIndex);
  });

  useEffect(() => {
    if (settlementsQuery.loading === true) {
      setHasSettlementOffer(false);
      setHasScheduledPayment(false);
      setHasPendingPayment(false);
      setHasExpiredOffer(false);
      setHasCompletedOffer({ offerType: null });
    }
  }, [settlementsQuery.loading]);

  useEffect(() => {
    if (settlementsQuery.data) {
      processSettlementsOffersData(settlementsQuery.data);
    }
  }, [settlementsQuery.data]);

  const upcomingPaymentActivityQuery = useQuery(GET_UPCOMING_PAYMENT_ACTIVITY, {
    variables: { customerId, accountId },
    fetchPolicy: 'no-cache',
  });

  useEffect(() => {
    const account = upcomingPaymentActivityQuery.data?.account;
    if (account) {
      const hasAutopaySchedule = !!account?.autopay?.length;
      const hasUpcomingPayment = !!account?.upcomingPayments?.find(
        (upcomingPayment) => upcomingPayment.state === 'SCHEDULED',
      );
      const hasPendingPayment = !!account?.upcomingPayments?.find(
        (upcomingPayment) => upcomingPayment.state === 'PENDING',
      );

      setHasScheduledPayment(hasAutopaySchedule || hasUpcomingPayment);
      setHasPendingPayment(hasPendingPayment);
    }
  }, [upcomingPaymentActivityQuery.data]);

  const thirdParty = useThirdPartyInfo(accountId);

  const renderStep = () => {
    const StepComponent = currentStep?.component;
    if (StepComponent) {
      return (
        <StepComponent
          offers={offers}
          hasPendingPayment={hasPendingPayment}
          hasScheduledPayment={hasScheduledPayment}
          settlementsQuery={settlementsQuery}
          thirdParty={thirdParty}
        />
      );
    }
  };

  const offerDashboard = useMemo(() => {
    if (!settlementsQuery.data) {
      return null;
    }

    // we always want to show settlement offers (includes pending close out, fulfilled, expired),
    // regardless of feature flag status
    // but if the flag is off (i.e. we are no longer offering settlement offers to customers),
    // we want to show the agents a message that the feature is not available

    if (hasSettlementOffer) {
      return <ActiveOffer thirdParty={thirdParty} />;
    }

    if (hasExpiredOffer) {
      return (
        <SectionMessage
          status={STATUS.ERROR}
          icon={FrownOutlined}
          text="The account did not complete their settlement offer."
          buttons={[
            {
              text: 'Check for another available offer',
              onClick: () => setHasExpiredOffer(false),
            },
          ]}
        />
      );
    }

    if (!hasSettlementOffer && hasEligibleSettlementsOffer) {
      return (
        <OfferSetup
          renderStep={renderStep}
          thirdParty={thirdParty}
          hasInitiatedSettlementOfferFromAppWeb={
            hasInitiatedSettlementOfferFromAppWeb
          }
        />
      );
    }

    if (!hasSettlementOffer && !hasEligibleSettlementsOffer) {
      return (
        <>
          <SectionMessage
            status={STATUS.WARNING}
            text="This account is not eligible for a settlement offer."
            buttons={[
              {
                text: 'View Eligibility Criteria',
                onClick: offerEligibilityModal.show,
              },
            ]}
          />
          <OfferEligibilityModal
            {...offerEligibilityModal}
            eligibilityChecks={eligibilityChecks}
            eligible={hasEligibleSettlementsOffer}
            offer="Settlements"
          />
        </>
      );
    }
  }, [
    offer.offerId,
    offer.state,
    hasCompletedOffer,
    hasExpiredOffer,
    renderStep,
    hasEligibleSettlementsOffer,
    hasInitiatedSettlementOfferFromAppWeb,
    settlementsQuery.data,
  ]);

  useEffect(() => {
    resetRecoveriesState();
  }, [accountId, customerId]);

  const prevOfferIdRef = useRef();
  useEffect(() => {
    const prevOfferId = prevOfferIdRef.current;

    if (prevOfferId && offer?.offerId) {
      settlementsQuery.refetch();
    }

    prevOfferIdRef.current = offer?.offerId;
  }, [offer?.offerId]);

  return (
    <>
      <QueryBoundary
        query={settlementsQuery}
        mode={QueryBoundary.MODE.MESSAGE}
        loadingMessage={'Loading Programs'}
      >
        {offerDashboard}
      </QueryBoundary>
    </>
  );
};

export default RecoveriesSection;
